import { Parse } from 'parse';
Parse.initialize(process.env.REACT_APP_APP_ID, null, process.env.REACT_APP_MASTER_KEY);
Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
Parse.serverURL = process.env.REACT_APP_SERVER_URL;

const handleParseError = async (error) => {
  if (error.code === 209 || error.message?.includes('Invalid session token')) {
    try {
      await Parse.User.logOut();
    } catch (logoutError) {
      console.error('Error during logout:', logoutError);
    }

    localStorage.clear();
    window.location.href = '/login';
    throw new Error('Session expired. Please login again.');
  }
  throw error;
};

const shouldAuditAdminAction = () => localStorage.getItem('role') === 'Admin';
const AUDIT_LOG_CLASS = 'RgsActivityLog';

const sanitizeAuditPayload = (data = {}) => {
  const excluded = new Set([
    'password',
    'confirmPassword',
    'sessionToken',
    'ACL',
    'createdAt',
    'updatedAt',
    'objectId',
    'id',
    'className',
    '__type'
  ]);
  return Object.fromEntries(Object.entries(data).filter(([key]) => !excluded.has(key)));
};

const buildChangedFields = (before = {}, after = {}, keysToCompare = null) => {
  const fields = [];
  const keys = new Set(
    keysToCompare || [...Object.keys(before || {}), ...Object.keys(after || {})]
  );

  keys.forEach((key) => {
    if (['password', 'confirmPassword', 'sessionToken', 'ACL'].includes(key)) return;
    const beforeValue = before?.[key];
    const afterValue = after?.[key];

    if (JSON.stringify(beforeValue) !== JSON.stringify(afterValue)) {
      fields.push({
        field: key,
        from: beforeValue ?? null,
        to: afterValue ?? null
      });
    }
  });

  return fields;
};

const GAME_CATALOGUE_AUDIT_FIELDS = [
  'name',
  'category',
  'description',
  'type',
  'gameSequence',
  'building',
  'slug',
  'gameURL',
  'isActive',
  'live_version',
  'gameId'
];

const GAME_BET_CONFIG_AUDIT_FIELDS = [
  'gameSlug',
  'gameName',
  'betLevels',
  'minBet',
  'maxBet',
  'defaultBetLevel',
  'stepBet',
  'paytable',
  'gameCatalogueId'
];

const buildActorSnapshot = () => {
  const currentUser = Parse.User.current();
  return {
    actorId: currentUser?.id || 'system',
    actorName:
      currentUser?.get('name') || currentUser?.get('username') || currentUser?.id || 'system',
    actorEmail: currentUser?.get('email') || null
  };
};

const writeAdminAuditLog = async ({ action, targetType, targetId, section, details = {} }) => {
  if (!shouldAuditAdminAction()) {
    return;
  }

  try {
    const currentUser = Parse.User.current();
    if (!currentUser) {
      return;
    }

    const ActivityLog = Parse.Object.extend(AUDIT_LOG_CLASS);
    const log = new ActivityLog();
    const role = localStorage.getItem('role') || 'Admin';
    const actor = buildActorSnapshot();

    log.set('action', action);
    log.set('actorId', actor.actorId);
    log.set('actorRole', role);
    log.set('actorName', actor.actorName);
    log.set('actorEmail', actor.actorEmail);
    if (section) log.set('section', section);
    log.set('targetType', targetType);
    if (targetId) log.set('targetId', targetId);
    log.set('details', {
      ...details,
      actorName: actor.actorName,
      actorEmail: actor.actorEmail,
      actorId: actor.actorId,
      section: section || details.section || 'admin-panel',
      source: 'frontend-admin'
    });

    await log.save(null, { useMasterKey: true });
  } catch (error) {
    console.error('[AdminAudit] Failed to write log:', error.message);
  }
};

export const dataProvider = {
  create: async (resource, params) => {
    try {
      let Resource = null;
      if (resource === 'GameCatalogue') {
        // Parse JS SDK can't reliably create with a custom objectId from the
        // client (setting .id triggers an update path → "Object not found").
        // Delegate creation to the createGameCatalogue cloud function which
        // handles both the auto-id and custom-id cases server-side with the
        // master key.
        const {
          objectId,
          gameId,
          name,
          category,
          description,
          type,
          gameSequence,
          building,
          slug,
          gameURL
        } = params.data;

        const result = await Parse.Cloud.run('createGameCatalogue', {
          objectId: objectId ? String(objectId).trim() : undefined,
          gameId: gameId ? Number(gameId) : undefined,
          name,
          category,
          description,
          type,
          gameSequence:
            gameSequence !== undefined && gameSequence !== '' ? Number(gameSequence) : undefined,
          building,
          slug: slug ? String(slug).trim() : undefined,
          gameURL: gameURL ? String(gameURL).trim() : undefined
        });

        await writeAdminAuditLog({
          action: 'admin_create',
          targetType: resource,
          targetId: result.id || result.objectId,
          section: resource,
          details: {
            resource,
            summary: `Created ${resource}`,
            changedFields: buildChangedFields(
              {},
              sanitizeAuditPayload(params.data),
              Object.keys(sanitizeAuditPayload(params.data))
            ),
            payload: sanitizeAuditPayload(params.data)
          }
        });

        return { data: { id: result.id || result.objectId, ...result } };
      } else {
        Resource = Parse.Object.extend(resource);
      }
      const obj = new Resource();
      const result = await obj.save(params.data);

      await writeAdminAuditLog({
        action: 'admin_create',
        targetType: resource,
        targetId: result.id,
        section: resource,
        details: {
          resource,
          summary: `Created ${resource}`,
          changedFields: buildChangedFields(
            {},
            sanitizeAuditPayload(params.data),
            Object.keys(sanitizeAuditPayload(params.data))
          ),
          payload: sanitizeAuditPayload(params.data)
        }
      });

      return { data: { id: result.id, ...result.attributes } };
    } catch (error) {
      return handleParseError(error);
    }
  },
  getOne: async () => {},
  getList: async (resource, params) => {
    Parse.masterKey = process.env.REACT_APP_MASTER_KEY;
    const { page, perPage } = params.pagination;
    const { field, order } = params.sort;
    const filter = params.filter;

    let query;
    let count;

    if (resource === 'GameBetConfig') {
      const Resource = Parse.Object.extend('GameBetConfig');
      query = new Parse.Query(Resource);
      query.include('gameCatalogueId');
      if (filter?.gameName) {
        query.matches('gameName', filter.gameName, 'i');
      }
    } else if (resource === 'GameCatalogue') {
      const Resource = Parse.Object.extend(resource);
      query = new Parse.Query(Resource);
      query.include('building');
      if (filter) {
        Object.keys(filter).forEach((f) => {
          if (typeof filter[f] === 'string') {
            query.matches(f, filter[f], 'i');
          } else {
            query.equalTo(f, filter[f]);
          }
        });
      }
    } else {
      const Resource = Parse.Object.extend(resource);
      query = new Parse.Query(Resource);
      if (filter) {
        Object.keys(filter).forEach((f) => {
          if (typeof filter[f] === 'string') {
            query.matches(f, filter[f], 'i');
          } else {
            query.equalTo(f, filter[f]);
          }
        });
      }
    }

    count = await query.count({ useMasterKey: true });

    query.skip((page - 1) * perPage);
    query.limit(perPage);

    if (field && order) {
      const sortField = field === 'id' ? 'objectId' : field;
      if (order === 'DESC') {
        query.descending(sortField);
      } else if (order === 'ASC') {
        query.ascending(sortField);
      }
    }

    try {
      const results = await query.find({ useMasterKey: true });
      const visibleResults =
        resource === 'GameCatalogue' ? results : results.filter((o) => o.get('isActive') !== false);

      const data = visibleResults.map((o) => {
        if (resource === 'GameBetConfig') {
          const betLevels = o.get('betLevels') || [];
          return {
            id: o.id,
            gameSlug: o.get('gameSlug'),
            gameName: o.get('gameName'),
            betLevels,
            minBet: o.get('minBet'),
            maxBet: o.get('maxBet'),
            defaultBetLevel: o.get('defaultBetLevel'),
            stepBet: o.get('stepBet'),
            updatedAt: o.get('updatedAt')
          };
        } else if (resource === 'GameCatalogue') {
          return {
            id: o.id,
            name: o.get('name'),
            category: o.get('category'),
            description: o.get('description'),
            type: o.get('type'),
            objectId: o.get('objectId'),
            gameId: o.get('gameId'),
            gameSequence: o.get('gameSequence'),
            isActive: o.get('isActive'),
            live_version: o.get('live_version'),
            gameURL: o.get('gameURL'),
            slug: o.get('slug'),
            createdAt: o.get('createdAt'),
            updatedAt: o.get('updatedAt'),
            building: o.get('building')
              ? {
                  id: o.get('building').id,
                  name: o.get('building').get('name')
                }
              : null
          };
        }
        return { id: o.id, ...o.attributes };
      });

      return {
        data,
        total: count
      };
    } catch (error) {
      return handleParseError(error);
    }
  },
  update: async (resource, params) => {
    let query = null;
    let obj = null;
    let r = null;

    const excludeFields = [
      'id',
      'sessionToken',
      'createdAt',
      'updatedAt',
      'ACL',
      'confirmPassword'
    ];

    const data = Object.keys(params.data)
      .filter((key) => !excludeFields.includes(key))
      .reduce((result, key) => {
        result[key] = params.data[key];
        return result;
      }, {});

    try {
      if (resource === 'GameCatalogue') {
        const Resource = Parse.Object.extend(resource);
        query = new Parse.Query(Resource);
        obj = await query.get(params.id, { useMasterKey: true });
        const before = obj.toJSON();
        if (data.building) {
          const Building = Parse.Object.extend('VegasBuilding');
          const buildingPointer = Building.createWithoutData(data.building);
          obj.set('building', buildingPointer);
          delete data.building;
        }
        r = await obj.save(data, { useMasterKey: true });

        await writeAdminAuditLog({
          action: 'admin_update',
          targetType: resource,
          targetId: r.id,
          section: resource,
          details: {
            resource,
            summary: `Updated ${resource}`,
            changedFields: buildChangedFields(
              before,
              sanitizeAuditPayload(data),
              Object.keys(sanitizeAuditPayload(data))
            ),
            payload: sanitizeAuditPayload(data)
          }
        });

        return { data: { id: r.id, ...r.attributes } };
      } else if (resource === 'gameCatalogueSwitch') {
        const GameCatalogue = Parse.Object.extend('GameCatalogue');
        const switchQuery = new Parse.Query(GameCatalogue);
        const gameCatalogueObject = await switchQuery.get(params.id);
        const before = gameCatalogueObject.toJSON();

        if (params?.data?.isActive !== undefined) {
          gameCatalogueObject.set('isActive', params.data.isActive);
        }

        const result = await gameCatalogueObject.save();

        await writeAdminAuditLog({
          action: 'admin_update',
          targetType: resource,
          targetId: result.id,
          section: resource,
          details: {
            resource,
            summary: `Updated ${resource}`,
            changedFields: buildChangedFields(before, sanitizeAuditPayload(params.data), [
              ...Object.keys(sanitizeAuditPayload(params.data))
            ]),
            payload: sanitizeAuditPayload(params.data)
          }
        });

        return { data: { id: result.id, ...result.attributes } };
      } else if (resource === 'updateGameBetConfig') {
        const {
          objectId,
          gameSlug,
          gameName,
          betLevels,
          minBet,
          maxBet,
          defaultBetLevel,
          stepBet
        } = params.data;
        const beforeQuery = new Parse.Query('GameBetConfig');
        const beforeRecord = await beforeQuery.get(params.id || objectId, { useMasterKey: true });
        const result = await Parse.Cloud.run('saveGameBetConfig', {
          objectId,
          gameSlug,
          gameName,
          betLevels,
          minBet,
          maxBet,
          defaultBetLevel,
          stepBet
        });

        await writeAdminAuditLog({
          action: 'admin_update',
          targetType: 'GameBetConfig',
          targetId: result.objectId,
          section: 'GameBetConfig',
          details: {
            resource: 'GameBetConfig',
            summary: 'Updated GameBetConfig',
            changedFields: buildChangedFields(
              beforeRecord.toJSON(),
              sanitizeAuditPayload(params.data),
              Object.keys(sanitizeAuditPayload(params.data))
            ),
            payload: sanitizeAuditPayload(params.data)
          }
        });

        return { data: { id: result.objectId, ...params.data } };
      } else {
        const Resource = Parse.Object.extend(resource);
        query = new Parse.Query(Resource);
        obj = await query.get(params.id, { useMasterKey: true });
        const before = obj.toJSON();
        r = await obj.save(data, { useMasterKey: true });

        await writeAdminAuditLog({
          action: 'admin_update',
          targetType: resource,
          targetId: r.id,
          section: resource,
          details: {
            resource,
            summary: `Updated ${resource}`,
            changedFields: buildChangedFields(
              before,
              sanitizeAuditPayload(data),
              Object.keys(sanitizeAuditPayload(data))
            ),
            payload: sanitizeAuditPayload(data)
          }
        });
      }
      return { data: { id: r.id, ...r.attributes } };
    } catch (error) {
      console.error('Error updating resource:', error);
      throw new Error(error.message);
    }
  },
  getMany: async (resource, params) => {
    let results = null;

    if (resource === 'GameCatalogue') {
      results = params.ids
        .map((obj) => {
          if (typeof obj === 'string')
            return new Parse.Query('VegasBuilding').get(obj, {
              useMasterKey: true
            });
        })
        .filter((n) => n);
    } else {
      const Resource = Parse.Object.extend(resource);
      results = params.ids.map((id) => new Parse.Query(Resource).get(id));
    }
    const data = await Promise.all(results);
    return {
      data: data.map((o) => ({ id: o.id, ...o.attributes }))
    };
  },
  delete: async (resource, params) => {
    let query = null;
    let result = null;
    let data = null;
    try {
      const Resource = Parse.Object.extend(resource);
      query = new Parse.Query(Resource);
      result = await query.get(params.id);
      data = { data: { id: result.id, ...result.attributes } };
      const before = result.toJSON();
      const actorRole = localStorage.getItem('role') || null;
      const isAdmin = String(actorRole || '').toLowerCase() === 'admin';

      if (isAdmin) {
        if (Object.prototype.hasOwnProperty.call(result.attributes, 'isActive')) {
          result.set('isActive', false);
        }
        await result.save(null, { useMasterKey: true });
      } else {
        await result.destroy({ useMasterKey: true });
      }

      await writeAdminAuditLog({
        action: 'admin_delete',
        targetType: resource,
        targetId: result.id,
        section: resource,
        details: {
          resource,
          summary: `${isAdmin ? 'Soft deleted' : 'Deleted'} ${resource}`,
          changedFields: buildChangedFields(before, {}),
          payload: sanitizeAuditPayload(result.attributes)
        }
      });

      return data;
    } catch (error) {
      return handleParseError(error);
    }
  }
};
